import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ShieldAlert, Loader2 } from "lucide-react";
import { Link } from "wouter";

import { useUser } from "@/hooks/useUser";
import { useAppTranslation } from "@/i18n/useAppTranslation";

interface MfaStatus {
  policy: { requirement: "required" | "nudge" | "optional" };
  state: { hasTotp: boolean; hasSms: boolean; aal: "aal1" | "aal2" | null };
  satisfied: boolean;
  showHostNudge: boolean;
  recoveryCodes: { total: number; remaining: number };
}

/**
 * Task #3063 — Admin route MFA gate (client-side).
 *
 * The server-side `requireAdmin` middleware already refuses admin API
 * calls with `mfa_required` for un-enrolled or aal1 admins, so this
 * gate is primarily a UX layer: it stops the admin shell from
 * rendering a wall of broken data fetches and instead routes the
 * admin straight into enrolment (or the TOTP challenge if they have
 * a factor but the current session is still aal1).
 *
 * Task #3526 — when the admin already has a verified TOTP factor but
 * the current session is only aal1 (e.g. they reloaded past the
 * post-login overlay, or a background proxy refresh dropped them back
 * to aal1), we render an inline 6-digit code prompt here. Submitting a
 * valid code re-elevates the session to aal2 IN PLACE via
 * `stepUpAdminSession` — which also adopts the aal2 refresh token into
 * the HttpOnly cookie so it sticks across reloads — and then re-checks
 * the status with the freshly-minted token. No sign-out / sign-in round
 * trip required. The "no factor enrolled" branch still routes to
 * settings for enrolment.
 */
export function AdminMfaGate({ children }: { children: ReactNode }) {
  const { t } = useAppTranslation();
  const { session, isAdmin, stepUpAdminSession } = useUser();
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Lost-device fallback: a TOTP-enrolled admin who can't produce a live
  // authenticator code can switch to a saved recovery code instead.
  const [mode, setMode] = useState<"totp" | "recovery">("totp");

  const accessToken = session?.access_token;

  // Refetch /auth/mfa/status. `tokenOverride` lets the step-up handler
  // re-check with the just-elevated access token immediately, instead of
  // waiting for the AuthProvider session state to round-trip through
  // onAuthStateChange (which would otherwise refetch with the stale aal1
  // token and keep the gate up for an extra render).
  const loadStatus = useCallback(
    async (tokenOverride?: string) => {
      const token = tokenOverride ?? accessToken;
      if (!token || !isAdmin) {
        setLoaded(true);
        return;
      }
      const apiBase = import.meta.env.BASE_URL || "/";
      try {
        const r = await fetch(`${apiBase}api/auth/mfa/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(r.ok ? ((await r.json()) as MfaStatus) : null);
      } catch {
        setStatus(null);
      } finally {
        setLoaded(true);
      }
    },
    [accessToken, isAdmin],
  );

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleStepUp = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const cleaned = code.replace(/\D/g, "").slice(0, 6);
      if (cleaned.length !== 6) {
        setError("Enter the 6-digit code from your authenticator app.");
        return;
      }
      setSubmitting(true);
      setError(null);
      const { error: stepErr, accessToken: elevated } = await stepUpAdminSession(cleaned);
      if (stepErr) {
        setError(stepErr.message || "That code didn't work. Please try again.");
        setSubmitting(false);
        return;
      }
      setCode("");
      // Re-check with the elevated token so the gate clears in the same tick.
      await loadStatus(elevated);
      setSubmitting(false);
    },
    [code, stepUpAdminSession, loadStatus],
  );

  // Lost-device recovery — burn a saved recovery code instead of a live
  // authenticator code. The server (POST /auth/mfa/recovery-codes/consume)
  // verifies the code and stamps a short `mfa_recovery_bypass_until` window;
  // /auth/mfa/status then reports `satisfied` for this same aal1 token, so a
  // plain refetch clears the gate and the admin can reach Settings to enrol a
  // fresh authenticator. We do NOT elevate Supabase's AAL — the server-side
  // bypass is the only thing required, and requireAdmin honours it identically.
  const handleRecovery = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const trimmed = code.trim();
      if (trimmed.length < 8) {
        setError("Enter one of the recovery codes you saved at setup.");
        return;
      }
      if (!accessToken) {
        setError("Your session expired. Please sign in again.");
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        const apiBase = import.meta.env.BASE_URL || "/";
        const res = await fetch(`${apiBase}api/auth/mfa/recovery-codes/consume`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ code: trimmed }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          setError(
            body?.error === "invalid_code"
              ? "That recovery code doesn't match (or was already used)."
              : "Couldn't verify that recovery code right now.",
          );
          setSubmitting(false);
          return;
        }
        setCode("");
        await loadStatus();
        setSubmitting(false);
      } catch {
        setError("Network error. Please try again.");
        setSubmitting(false);
      }
    },
    [code, accessToken, loadStatus],
  );

  if (!isAdmin) return <>{children}</>;
  if (!loaded) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!status || status.satisfied) return <>{children}</>;

  const needsEnrol = !status.state.hasTotp;
  const canStepUp = status.state.hasTotp && status.state.aal !== "aal2";

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-6 w-6 text-amber-600" />
          <div className="w-full">
            <h2 className="text-lg font-semibold text-amber-900">
              {t("auth.admin_mfa_required_title")}
            </h2>
            <p className="mt-2 text-sm text-amber-800">
              {needsEnrol ? t("auth.admin_mfa_enrol_body") : t("auth.admin_mfa_stepup_body")}
            </p>

            {canStepUp ? (
              <div className="mt-4">
                {mode === "totp" ? (
                  <form onSubmit={handleStepUp}>
                    <label htmlFor="admin-mfa-code" className="sr-only">
                      {t("auth.admin_mfa_code_label")}
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="admin-mfa-code"
                        name="admin-mfa-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={6}
                        autoFocus
                        value={code}
                        onChange={(e) => {
                          setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                          if (error) setError(null);
                        }}
                        placeholder="123456"
                        disabled={submitting}
                        className="w-32 rounded-lg border border-amber-300 bg-white px-3 py-2 text-center text-lg tracking-[0.3em] font-semibold text-amber-900 outline-none focus:border-amber-500 disabled:opacity-60"
                      />
                      <button
                        type="submit"
                        disabled={submitting || code.length !== 6}
                        className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {t("auth.admin_mfa_verify_continue")}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleRecovery}>
                    <p className="mb-2 text-sm text-amber-800">{t("auth.mfa_recovery_prompt")}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="admin-mfa-recovery"
                        name="admin-mfa-recovery"
                        type="text"
                        autoComplete="off"
                        autoFocus
                        aria-label={t("auth.mfa_recovery_code_aria_label")}
                        value={code}
                        onChange={(e) => {
                          setCode(e.target.value);
                          if (error) setError(null);
                        }}
                        placeholder="xxxx-xxxx"
                        disabled={submitting}
                        className="w-44 rounded-lg border border-amber-300 bg-white px-3 py-2 text-center font-mono text-amber-900 outline-none focus:border-amber-500 disabled:opacity-60"
                      />
                      <button
                        type="submit"
                        disabled={submitting || code.trim().length < 8}
                        className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {t("auth.admin_mfa_verify_continue")}
                      </button>
                    </div>
                  </form>
                )}
                {error ? (
                  <p role="alert" className="mt-2 text-sm font-medium text-red-700">
                    {error}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === "totp" ? "recovery" : "totp");
                    setError(null);
                    setCode("");
                  }}
                  className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-amber-800 underline-offset-2 hover:underline"
                >
                  {mode === "totp" ? t("auth.mfa_use_recovery") : t("auth.mfa_use_authenticator")}
                </button>
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              {needsEnrol ? (
                <Link
                  href="/settings#security"
                  className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  {t("auth.setup_two_factor")}
                </Link>
              ) : null}
              <Link
                href="/"
                className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
              >
                {t("auth.back_to_site")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
